import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  concat,
  EMPTY,
  from,
  ignoreElements,
  mergeMap,
  of,
  switchMap
} from 'rxjs'
import { createStagedOrdersFromUploadRow } from '../../components/Upload/helpers'
import { getHub } from '../../helpers/hub'
import {
  applyTempCheckedOrders,
  createTempCheckedOrders
} from '../checkedOrders/actions'
import { defaultFilter } from '../filter/types'
import { setMyOrdersOpen } from '../order/actions'
import {
  fetchSecuritiesByIds,
  setIsMine,
  setWatchListId
} from '../securities/actions'
import { addOrUpdateStagedOrders } from '../stagedOrders/actions'
import { StagedOrder } from '../stagedOrders/types'
import {
  addSecuritiesToWatchlist,
  FetchWatchListsSuccessAction
} from '../watchList/actions'
import { getResponseForPermission } from '../watchList/helpers'
import { logError } from '../ws/actions'
import {
  CreateNewWatchlistWithSecurityIdsAction,
  createWatchlistFromWatchlistInfo,
  CreateWatchlistFromWatchlistInfoAction,
  CreateWatchlistWithEnhancedUploadRowsAction,
  FetchSymbolDetailsAction,
  logInvalidIdentifiers,
  LogInvalidIdentifiersAction,
  resetNewWatchlistTransactionId,
  setNewWatchlistTransactionId,
  toggleDropdownState,
  UploadEnhancedOrderRowsAction,
  uploadOrdersFailure,
  UploadOrdersFromPreviousDate,
  uploadOrdersServerError
} from './actions'
import { getSecurityIdsFromIdentifier$ } from './helpers'
import { getNewWatchlistTransactionId } from './selectors'
import { DropdownState, ErrorInfo } from './types'

const createNewWatchlistWithSecurityIdsEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('upload.createNewWatchlistWithSecurityIds'),
    mergeMap((action: CreateNewWatchlistWithSecurityIdsAction) => {
      const {
        gridIndex,
        name,
        permission,
        book,
        identifiers,
        filter,
        myFirmChecked,
        size,
        useAdvancedFilter
      } = action.payload

      return getHub()
        .invoke(
          'CreateWatchlist',
          name,
          identifiers,
          permission,
          book.name,
          filter,
          myFirmChecked,
          size,
          useAdvancedFilter,
          0
        )
        .pipe(
          mergeMap((id: number) =>
            of(
              setNewWatchlistTransactionId(gridIndex, id),
              setWatchListId(gridIndex, id),
              toggleDropdownState(gridIndex, 'closed')
            )
          ),
          catchError((err) =>
            of(uploadOrdersServerError(`${err.message}`), logError(err))
          )
        )
    })
  )

const setCreatedWatchlistAsCurrentWatchlistEpic: Epic<Action> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<FetchWatchListsSuccessAction>('watchList.fetchWatchListsSuccess'),
    mergeMap((action) => {
      const { gridIndex, watchlists } = action.payload
      const { transactionId, id: watchlistId } = watchlists[0]

      const newWatchlistTransactionId = getNewWatchlistTransactionId(
        state$.value
      )(gridIndex)

      if (newWatchlistTransactionId === transactionId && gridIndex >= 0) {
        return of(
          setWatchListId(gridIndex, watchlistId),
          resetNewWatchlistTransactionId(gridIndex)
        )
      }

      return EMPTY
    })
  )

const uploadOrdersFromPreviousDateEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('upload.uploadOrdersFromPreviousDate'),
    mergeMap((action: UploadOrdersFromPreviousDate) => {
      const stagedOrders = action.payload
      return concat(
        of(addOrUpdateStagedOrders(stagedOrders, true)),
        of(
          createTempCheckedOrders(
            0,
            stagedOrders.map((stagedOrder) => ({
              securityId: stagedOrder.securityId,
              orderType: stagedOrder.orderType
            }))
          )
        ),
        of(applyTempCheckedOrders())
      )
    })
  )

// ------------------ enhanced orders ------------------ //
const prepareWatchlistForUploadEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('upload.createWatchlistWithEnhancedUploadRows'),
    mergeMap((action: CreateWatchlistWithEnhancedUploadRowsAction) => {
      const { watchlist, gridIndex, book } = action.payload
      const watchlistInfo = {
        gridIndex,
        name: watchlist.name,
        permission: getResponseForPermission(
          watchlist.permission === 'Private' ? 'private' : 'myFirm'
        ),
        book,
        filter: defaultFilter,
        myFirmChecked: false,
        size: 0,
        useAdvancedFilter: false
      } as const
      const identifiers: string[] = watchlist.rows.map(
        (row) => row.identifier?.toUpperCase() || ''
      )
      return getSecurityIdsFromIdentifier$(identifiers).pipe(
        mergeMap((secIds) => {
          const create = createWatchlistFromWatchlistInfo(
            watchlistInfo,
            identifiers
          )
          const ordersByRow: StagedOrder[][] = []
          const errors: ErrorInfo[] = []
          secIds.forEach((ids, i) => {
            if (ids.length === 1) {
              ordersByRow.push(
                createStagedOrdersFromUploadRow(
                  ids[0],
                  watchlist.rows[i],
                  book?.id
                )
              )
            }
            if (!ids.length) {
              errors.push({
                index: i,
                identifier: identifiers[i],
                errorIndex: i
              })
            }
          })

          const toggleState: DropdownState = errors.length ? 'upload' : 'closed'
          const toggle = toggleDropdownState(gridIndex, toggleState)
          if (errors.length) {
            return of(
              uploadOrdersFailure(errors),
              logInvalidIdentifiers(errors.map((e) => e.identifier))
            )
          }
          const successActions: Action[] = [create, toggle]
          const orders = ordersByRow.flat()
          if (orders.length) {
            successActions.push(
              addOrUpdateStagedOrders(orders, true),
              setIsMine(gridIndex, true),
              setMyOrdersOpen(gridIndex, true),
              createTempCheckedOrders(
                0,
                orders.map(({ securityId, orderType }) => ({
                  securityId,
                  orderType
                }))
              )
            )
          }
          return from(successActions)
        }),
        catchError((err) =>
          of(uploadOrdersServerError(`${err.message}`), logError(err))
        )
      )
    })
  )

const createWatchlistFromWatchlistInfoEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('upload.createWatchlistFromWatchlistInfo'),
    mergeMap((action: CreateWatchlistFromWatchlistInfoAction) => {
      const {
        name,
        permission,
        book,
        filter,
        myFirmChecked,
        useAdvancedFilter,
        size,
        gridIndex
      } = action.payload.watchlist
      const { userId, identifiers } = action.payload
      return getHub()
        .invoke(
          'CreateWatchlist',
          name,
          identifiers,
          permission,
          book?.name ?? '',
          filter,
          myFirmChecked,
          size,
          useAdvancedFilter,
          userId
        )
        .pipe(
          mergeMap((transactionId: number) => {
            return of(setNewWatchlistTransactionId(gridIndex, transactionId))
          }),
          catchError((err) =>
            of(uploadOrdersServerError(`${err.message}`), logError(err))
          )
        )
    })
  )

const uploadEnhancedOrderRowsEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('upload.uploadEnhancedOrderRows'),
    mergeMap((action: UploadEnhancedOrderRowsAction) => {
      const { gridIndex, watchlist, rows, bookId } = action.payload
      const correctedRows = rows.map((r) => ({
        ...r,
        identifier: r.identifier?.toUpperCase()
      }))
      const identifiers = correctedRows.map((row) => row.identifier || '')
      return getSecurityIdsFromIdentifier$(identifiers).pipe(
        mergeMap((secIds) => {
          const ordersByRow: StagedOrder[][] = []
          const actions: Action[] = []
          const securitiesToAdd: number[] = []
          const identifiersToAdd: string[] = []
          const errors: ErrorInfo[] = []
          secIds.forEach((ids, i) => {
            if (ids.length === 1) {
              ordersByRow.push(
                createStagedOrdersFromUploadRow(
                  ids[0],
                  correctedRows[i],
                  bookId
                )
              )
            }
            if (ids.length) {
              let added = false
              ids.forEach((id) => {
                // if (!watchlist.securityIds.includes(id)) {
                securitiesToAdd.push(id)
                added = true
                // }
              })
              if (added) {
                identifiersToAdd.push(correctedRows[i].identifier || '')
              }
            } else {
              errors.push({
                index: i,
                identifier: identifiers[i],
                errorIndex: i
              })
            }
          })
          if (errors.length) {
            return of(
              uploadOrdersFailure(errors),
              toggleDropdownState(0, 'upload'),
              logInvalidIdentifiers(errors.map((e) => e.identifier))
            )
          }
          if (securitiesToAdd.length) {
            actions.push(
              addSecuritiesToWatchlist(
                gridIndex,
                watchlist.id!,
                securitiesToAdd,
                identifiersToAdd
              )
            )
          }
          const orders = ordersByRow.flat()
          if (orders.length) {
            actions.push(
              addOrUpdateStagedOrders(orders),
              setIsMine(gridIndex, true),
              setMyOrdersOpen(gridIndex, true),
              createTempCheckedOrders(
                0,
                orders.map(({ securityId, orderType }) => ({
                  securityId,
                  orderType
                }))
              )
            )
          }
          actions.push(toggleDropdownState(gridIndex, 'closed'))
          return actions
        }),
        catchError((err) =>
          of(uploadOrdersServerError(`${err.message}`), logError(err))
        )
      )
    })
  )

const fetchSymbolDetailsEpic: Epic = (action$) =>
  action$.pipe(
    ofType<FetchSymbolDetailsAction>('upload.fetchSymbolDetails'),
    switchMap((action) => {
      return getSecurityIdsFromIdentifier$(action.payload).pipe(
        mergeMap((ids) => of(fetchSecuritiesByIds(ids.flat()))),
        catchError((err) => of(logError(err)))
      )
    })
  )

const logInvalidIdentifiersEpic: Epic = (action$) =>
  action$.pipe(
    ofType<LogInvalidIdentifiersAction>('upload.logInvalidIdentifiers'),
    mergeMap((action) => {
      const { identifiers } = action.payload
      if (!identifiers.length) return EMPTY
      return getHub()
        .invoke('LogInvalidIdentifiers', identifiers)
        .pipe(
          ignoreElements(),
          catchError((err) => of(logError(err)))
        )
    })
  )

export default combineEpics(
  setCreatedWatchlistAsCurrentWatchlistEpic,
  uploadOrdersFromPreviousDateEpic,
  createNewWatchlistWithSecurityIdsEpic,
  prepareWatchlistForUploadEpic,
  createWatchlistFromWatchlistInfoEpic,
  uploadEnhancedOrderRowsEpic,
  fetchSymbolDetailsEpic,
  logInvalidIdentifiersEpic
)
